/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// 🔐 Permissions Store — single source of truth for "what can the
/// signed-in user do, right now?"
///
/// Inputs (all live):
///   1. /superAdmins/{uid}                                 → SuperAdmin pseudo-role
///   2. users/{uid}.accounts.{accountId}.roleId            → role assignment for active account
///   3. /accounts/{accountId}/roles/{roleId}.permissions   → role's permission set
///
/// Output:
///   - `granted: Set<string>`        flat permission keys
///   - `can(key)`, `cannot(key)`     ergonomic checks
///   - `isAdmin`, `isSuperAdmin`     common shortcuts
///
/// The store is reactive on the underlying Firestore docs via the
/// existing useDocStore subscriptions, so a SuperAdmin getting added in
/// another tab, or an admin changing a custom role's permissions,
/// reflects immediately without a page reload.
///
/// Security note: this store is the *UI gate* — it controls what the
/// browser shows. Real enforcement lives in Firestore security rules
/// (firestore.rules) and any server-side function that mutates state.
/// A determined user can edit `granted` via devtools and unhide the
/// admin pages, but they will not be able to read or write data they
/// don't actually have rules-level access to.
/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

import { defineStore } from 'pinia'
import type { Firestore } from 'firebase/firestore'
import { collection, doc, onSnapshot } from 'firebase/firestore'

import { expandImplied, isPermissionKey, PERMISSIONS_BY_KEY } from '~/constants/permissions'
import { ADMIN_ROLE, SUPER_ADMIN_ROLE, isBuiltInRoleId } from '~/constants/builtInRoles'
import type { AccountRoleDoc } from '~/types/typesPermissions'

interface RolesById {
  [roleId: string]: AccountRoleDoc
}

export const usePermissionsStore = defineStore('permissionsStore', () => {
  const globalsStore = useGlobalsStore()
  const { accountId, userId, userProfile } = storeToRefs(globalsStore)

  // ────────────────────────────────────────────────────────────────────
  // Live inputs from Firestore. These are populated by `bind()` once
  // the user has both an accountId and a userId. The auth plugin calls
  // bind() after Firebase finishes hydrating; we also re-bind whenever
  // the active accountId changes (SuperAdmin tenant impersonation).
  // ────────────────────────────────────────────────────────────────────
  const rolesById = ref<RolesById>({})
  /**
   * True if the user has a doc at /superAdmins/{uid}. This is the
   * canonical source going forward. The `isSuperAdmin` computed below
   * also honors the legacy `users/{uid}.isSuperAdmin === true` field
   * for backward-compat with users who pre-date the migration to the
   * top-level /superAdmins collection.
   */
  const hasSuperAdminDoc = ref(false)
  const loaded = ref(false)
  const loading = ref(false)

  /** Snapshot unsubscribers — kept so we can rebind without leaking listeners. */
  let unsubRoles: (() => void) | null = null
  let unsubSuperAdmin: (() => void) | null = null

  function teardown() {
    if (unsubRoles) { unsubRoles(); unsubRoles = null }
    if (unsubSuperAdmin) { unsubSuperAdmin(); unsubSuperAdmin = null }
    rolesById.value = {}
    hasSuperAdminDoc.value = false
    loaded.value = false
  }

  /**
   * Bind live Firestore listeners for the currently-signed-in user
   * inside their currently-active account. Idempotent — calling it a
   * second time tears down prior listeners first.
   *
   * Called by the auth plugin after Firebase reports a user, and by
   * the SuperAdmin tenant-impersonation flow when the active accountId
   * changes.
   */
  async function bind() {
    teardown()
    const uid = userId.value
    const aId = accountId.value
    if (!uid) return // not signed in yet

    loading.value = true
    const { $firestore } = useNuxtApp()
    const fs = $firestore as Firestore

    // ── /superAdmins/{uid} ────────────────────────────────────────────
    // The legacy `users/{uid}.isSuperAdmin` field is also honored
    // (see the isSuperAdmin computed) for users who pre-date the
    // migration to this collection.
    unsubSuperAdmin = onSnapshot(
      doc(fs, 'superAdmins', uid),
      (snap) => {
        hasSuperAdminDoc.value = snap.exists()
      },
      (err) => {
        // A read failure here just means we treat the user as a
        // non-SuperAdmin via this path; the legacy user-doc flag may
        // still grant SuperAdmin status. Rules deny by default, which
        // is the safe fallback if both sources read empty.
        console.warn('[permissionsStore] /superAdmins read failed:', err.message)
        hasSuperAdminDoc.value = false
      },
    )

    // ── /accounts/{aId}/roles ─────────────────────────────────────────
    if (aId) {
      unsubRoles = onSnapshot(
        collection(fs, 'accounts', aId, 'roles'),
        (snap) => {
          const next: RolesById = {}
          snap.forEach((d) => {
            const data = d.data() as AccountRoleDoc
            next[d.id] = { ...data, id: d.id }
          })
          rolesById.value = next
          loaded.value = true
          loading.value = false
        },
        (err) => {
          console.warn('[permissionsStore] roles read failed:', err.message)
          rolesById.value = {}
          loaded.value = true
          loading.value = false
        },
      )
    }
    else {
      // No active account — likely mid-signup. Still flag as loaded so
      // gates resolve to "deny" promptly instead of spinning forever.
      loaded.value = true
      loading.value = false
    }
  }

  // ────────────────────────────────────────────────────────────────────
  // Computed: which roleId is the current user assigned in the active
  // account? Reads from the synced user doc (`accounts.{aid}.roleId`).
  // We accept the legacy `role` field as a fallback so users who
  // signed up before the migration ('owner' / 'admin') still resolve
  // to the admin built-in role.
  // ────────────────────────────────────────────────────────────────────
  const activeRoleId = computed<string | null>(() => {
    const aId = accountId.value
    if (!aId) return null
    const profile: any = userProfile.value
    const membership = profile?.accounts?.[aId]
    if (!membership) return null
    if (membership.roleId) return membership.roleId
    // Legacy compat: 'owner' and 'admin' both map to the new admin role.
    const legacy = String(membership.role || '').toLowerCase()
    if (legacy === 'owner' || legacy === 'admin') return 'admin'
    if (legacy === 'editor') return 'editor'
    if (legacy === 'viewer') return 'viewer'
    return null
  })

  /**
   * Unified SuperAdmin check. The user is a SuperAdmin if EITHER:
   *   - their uid is present at /superAdmins/{uid} (canonical), OR
   *   - their user doc has the legacy `isSuperAdmin: true` field
   *     (set by older versions of register_account.py).
   *
   * Honoring both lets users who pre-date the migration keep working
   * without anyone running a backfill script. New SuperAdmins should
   * be added via the /superAdmins collection — that's the path the
   * Firestore security rules trust for cross-tenant reads.
   */
  const isSuperAdmin = computed<boolean>(() => {
    if (hasSuperAdminDoc.value) return true
    const profile: any = userProfile.value
    return profile?.isSuperAdmin === true
  })

  /**
   * The flat permission set granted to the current user, expanded for
   * implications. Recomputes whenever any input changes.
   *
   * Resolution order:
   *   1. SuperAdmin → all permissions, full stop.
   *   2. Otherwise, look up the assigned roleId in rolesById.
   *   3. If the role doc isn't loaded yet but the id matches a built-in,
   *      fall back to the built-in's hardcoded permission set so we
   *      have a sensible answer the moment the user lands on a page
   *      (Firestore reads can lag a few ms behind navigation).
   */
  const granted = computed<Set<string>>(() => {
    if (isSuperAdmin.value) {
      return new Set(SUPER_ADMIN_ROLE.permissions)
    }
    const rId = activeRoleId.value
    if (!rId) return new Set()

    const live = rolesById.value[rId]
    if (live?.permissions?.length) {
      return new Set(expandImplied(live.permissions))
    }
    // Fallback: built-in role definitions hardcoded in client code.
    if (rId === 'admin') return new Set(ADMIN_ROLE.permissions)
    return new Set()
  })

  const isAdmin = computed(() => isSuperAdmin.value || activeRoleId.value === 'admin')

  /** Display name of the active role. */
  const activeRoleName = computed<string>(() => {
    if (isSuperAdmin.value) return SUPER_ADMIN_ROLE.name
    const rId = activeRoleId.value
    if (!rId) return ''
    const live = rolesById.value[rId]
    return live?.name || (isBuiltInRoleId(rId) ? rId.charAt(0).toUpperCase() + rId.slice(1) : rId)
  })

  // ────────────────────────────────────────────────────────────────────
  // Public check helpers. Accept either a single key or an array of
  // keys. The default is "any of the supplied permissions"; callers
  // who need "all of" pass mode='all'.
  // ────────────────────────────────────────────────────────────────────
  function can(keys: string | string[], mode: 'any' | 'all' = 'any'): boolean {
    if (isSuperAdmin.value) return true
    const list = Array.isArray(keys) ? keys : [keys]
    if (list.length === 0) return true
    const set = granted.value
    return mode === 'all'
      ? list.every(k => set.has(k))
      : list.some(k => set.has(k))
  }

  function cannot(keys: string | string[], mode: 'any' | 'all' = 'any'): boolean {
    return !can(keys, mode)
  }

  /**
   * Validate a list of permission keys before writing them to a role.
   * Strips unknown keys and SuperAdmin-only keys (which should never
   * be granted to an account-scoped role).
   */
  function sanitizeForAccountRole(keys: string[]): string[] {
    const out: string[] = []
    for (const k of keys) {
      if (!isPermissionKey(k)) continue
      // Disallow SuperAdmin-only perms on account-scoped roles. The
      // `superAdminOnly` flag in the permission catalog is the source
      // of truth — using it (instead of a `super:` prefix sniff) keeps
      // future SuperAdmin perms with non-`super:` keys safe.
      if (PERMISSIONS_BY_KEY[k]?.superAdminOnly) continue
      out.push(k)
    }
    return [...new Set(expandImplied(out))]
  }

  return {
    // state
    rolesById,
    loaded,
    loading,
    // computed
    activeRoleId,
    activeRoleName,
    granted,
    isAdmin,
    isSuperAdmin,
    // actions
    bind,
    teardown,
    can,
    cannot,
    sanitizeForAccountRole,
  }
})
